import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MediaTab, { mediaFormSchema } from '../components/course/MediaTab';
import ModulesTab from '../components/course/ModulesTab';
import SettingsTab, { settingsFormSchema } from '../components/course/SettingsTab';
import { CourseFormData, Subject } from '../components/course/types';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { ImageCropper } from '../components/ui/image-cropper';
import { useToast } from '../components/ui/use-toast';
import { storage, app } from "../firebase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from './../components/ui/tabs';
import { zodResolver } from '@hookform/resolvers/zod';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import GeneralInfoTab, { courseFormSchema } from '../components/course/GeneralInfoTab';
import { Alert, AlertDescription } from '../components/ui/alert';
import { collection, deleteDoc, doc, FirestoreError, getDoc, getDocs, getFirestore, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Combine form schemas for all tabs  
const courseSchema = z.object({
  ...courseFormSchema.shape,
  ...mediaFormSchema.shape,
  ...settingsFormSchema.shape,
});

type CourseFormValues = z.infer<typeof courseSchema>;

const EditCourse = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { courseId } = useParams(); // Get the course ID from the URL  

  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [currentSelectedSubject, setCurrentSelectedSubject] = useState(null);
  const [courseIdd, setCourseId] = useState<string | null>(courseId || null); // Set courseId from URL  
  const [allSubjects, setAllSubjects] = useState<any[]>([]); // State to hold the fetched subjects  
  const db = getFirestore(app);
  const auth = getAuth(app);

  const [batches, setBatches] = useState([]);
  const [courseCategories, setCourseCategories] = useState<any[]>([]); // State to hold course categories  
  // Sample subjects with modules  
  const [subjects, setSubjects] = useState<any[]>([]);

  // Fetch subjects from Firestore on component mount  
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "allSubjects"), (querySnapshot) => {
      const result = querySnapshot.docs.map((doc) => ({
        id: doc.id, // Get the document ID  
        ...doc.data(), // Spread the document data  
      })) as any[];

      const allSubjectList = result.map((x: any) => {
        return { ...x, subjectName: x.className }
      });

      setAllSubjects(allSubjectList); // Update state with fetched subjects  

    }, (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" }); // Handle errors  
    });

    const fetchBatchesData = async () => {
      setLoading(true); // Start loading before fetching data  

      try {
        const batchesCollection = collection(db, 'courseBatches'); // Reference the collection  
        const querySnapshot = await getDocs(batchesCollection); // Get all documents in the collection  

        // Map the documents to an array  
        const batchesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setBatches(batchesData.sort(sortByBatchName)); // Update state with batches  
      } catch (error: any) {
        if (error instanceof FirestoreError) {
          toast({
            title: "Error",
            description: "Failed to fetch batch data.",
            variant: "destructive",
          });
        }
      } finally {
        setLoading(false); // Stop loading after the attempt  
      }
    };

    const fetchCategories = async () => {
      try {
        setLoading(true); // Start loading state  
        const categoriesCollection = collection(db, 'courseCategories'); // Reference the collection  
        const querySnapshot = await getDocs(categoriesCollection); // Fetch documents from the collection  

        const categoriesData = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get document ID  
          ...doc.data(), // Spread document data  
        }));

        setCourseCategories(categoriesData); // Set the fetched data to state  
      } catch (error) {
        if (error instanceof FirestoreError) {
          console.log(error.message); // Set error message if fetching fails  
        } else {
          console.log("Failed to fetch course categories.");
        }
      } finally {
        setLoading(false); // Stop loading state  
      }
    };

    checkIsModuleEdited();
    fetchBatchesData();



    fetchCategories();

    return () => {
      unsubscribe(); // Clean up the listener on component unmount  
    };
  }, []);

  const sortByBatchName = (a: any, b: any) => {
    if (a.batchName < b.batchName) {
      return -1; // a should come before b  
    }
    if (a.batchName > b.batchName) {
      return 1; // a should come after b  
    }
    return 0; // a and b are equal  
  };

  const checkIsModuleEdited = () => {
    const isModulesActiveBack = JSON.parse(localStorage.getItem("isModulesActiveBack")) ?? {};
    if (isModulesActiveBack.isModulesActiveBack) {
      setActiveTab("modules");
    }
  }

  const onSubjectsChang = (subjects: any) => {
    setCurrentSelectedSubject({
      subjectId: subjects.subjectId,
      subjectName: subjects.subjectName,
      moduleIds: subjects.moduleIds
    });

    // const allSubjectsExceptModules = subjects.map(x => {
    //   return {
    //     subjectId: x.subjectId,
    //     subjectName: x.subjectName,
    //     moduleIds: x.moduleIds
    //   }
    // })
    // setSubjects(allSubjectsExceptModules);
    // handleSubmitAfterSubjects(form.getValues(), allSubjectsExceptModules);
  }

  const handleSubmitAfterSubjects = async (values: CourseFormValues, subjectsData: any) => {
    setLoading(true);

    try {
      // Combine form data with other necessary data  
      const courseData: any = {
        courseName: values.courseName,
        courseDescription: values?.courseDescription,
        category: values.category,
        duration: values?.duration ?? 120,
        level: values.level,
        price: values?.price ?? 25000,
        status: values?.status,
        thumbnail: { ...values.thumbnail },
        subjects: subjectsData.map(subject => ({
          subjectId: subject?.subjectId,
          subjectName: subject?.subjectName ?? '',
          moduleIds: subject?.moduleIds ? subject?.moduleIds : []
        })),
        introVideo: values?.introVideo ?? '',
        syllabusMaterial: values.syllabusMaterial || [],
        enrollmentLimit: values?.enrollmentLimit ?? 0,
        certificate: values?.certificate ?? '',
        instructor: values?.instructor ?? '',
        rating: Number(values?.rating) ?? 1
      };

      // Update the existing document in Firestore  
      const courseRef = doc(db, "courses", courseId); // Get a reference to the course document  
      await setDoc(courseRef, courseData); // Update the course data in Firestore  
      toast({
        title: "Course updated",
        description: `${courseData.courseName} has been successfully updated`,
      });

      console.log(courseData);

    } catch (error) {
      console.error("Error updating course:", error);
      toast({
        title: "Error",
        description: "Failed to update the course. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize the form with React Hook Form + Zod validation  
  const form = useForm<CourseFormValues>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: '',
      courseDescription: '',
      category: '',
      duration: 0,
      level: '',
      price: 0,
      status: 'draft',
      thumbnail: null,
      introVideo: null,
      syllabusMaterial: null,
      enrollmentLimit: '50',
      certificate: 'default',
      instructor: '',
      rating: null
    },
    mode: "onChange",
  });

  // Track form data for non-form components that still need the data  
  const [formData, setFormData] = useState<any>({
    resolver: zodResolver(courseSchema),
    courseName: '',
    courseDescription: '',
    category: '',
    duration: '',
    level: '',
    price: '',
    status: 'draft',
    thumbnail: null,
    enrollmentLimit: '50',
    certificate: 'default',
    thumbnailPreview: null,
    syllabusMaterial: null,
    introVideo: null,
    instructor: '',
    rating: null
  });

  // Update formData when form values change  
  useEffect(() => {
    const subscription = form.watch((value) => {
      setFormData(prev => ({
        ...prev,
        courseName: value.courseName || '',
        courseDescription: value.courseDescription || '',
        category: value.category || '',
        duration: value.duration || '',
        level: value.level || '',
        price: value.price || '',
        status: value.status || 'draft',
      }));
    });

    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Load course data when courseId is available  
  useEffect(() => {
    if (courseIdd) {
      const loadCourseData = async () => {
        try {
          const courseDoc = await getDoc(doc(db, "courses", courseId));
          if (courseDoc.exists()) {
            const courseData = courseDoc.data();
            // Populate the form with existing course data  
            form.reset({
              courseName: courseData.courseName,
              courseDescription: courseData.courseDescription,
              category: courseData.category,
              duration: Number(courseData.duration),
              level: courseData.level,
              price: Number(courseData.price),
              status: courseData.status,
              thumbnail: courseData.thumbnail,
              introVideo: courseData.introVideo,
              enrollmentLimit: courseData.enrollmentLimit,
              certificate: courseData.certificate,
              instructor: courseData?.instructor ?? '',
              syllabusMaterial: courseData?.syllabusMaterial ?? '',
              rating: Number(courseData?.rating ?? 1)
            });

            // Set the form data state as well  
            setFormData({
              courseName: courseData.courseName,
              courseDescription: courseData.courseDescription,
              category: courseData.category,
              duration: Number(courseData.duration),
              level: courseData.level,
              price: Number(courseData.price),
              status: courseData.status,
              thumbnail: courseData?.thumbnail,
              thumbnailPreview: courseData?.thumbnail?.url || null, // Presuming the thumbnail has a url  
              introVideo: courseData?.introVideo,
              subjects: eliminateDuplicateModuleIds(courseData)?.subjects ?? [],
              enrollmentLimit: courseData?.enrollmentLimit,
              certificate: courseData?.certificate,
              instructor: courseData?.instructor ?? '',
              syllabusMaterial: courseData?.syllabusMaterial ?? '',
              rating: Number(courseData?.rating ?? 1)
            });
            setSubjects(courseData?.subjects);
            setSubjects(eliminateDuplicateModuleIds(courseData)?.subjects);
          } else {
            toast({
              title: "Error",
              description: "Course not found.",
              variant: "destructive"
            });
            navigate(`/courses`); // Navigate back if course doesn't exist  
          }
        } catch (error) {
          console.error("Error fetching course data:", error);
          toast({
            title: "Error",
            description: "Failed to load course data.",
            variant: "destructive"
          });
        }
      };

      loadCourseData(); // Call the function to load data  
    }
  }, [courseId, db, form, navigate, toast]); // Dependencies for the useEffect  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type == 'number' ? Number(value) : value }));
    form.setValue(name as any, type == 'number' ? Number(value) : value);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    form.setValue(name as any, value);
  };

  const handleSubmit = async (values: CourseFormValues) => {
    setLoading(true);

    try {
      // Combine form data with other necessary data  
      const courseData: any = {
        courseName: values?.courseName || '', // Ensure it's not undefined  
        courseDescription: values?.courseDescription || '', // Ensure it's not undefined  
        category: values?.category || '', // Ensure it's not undefined  
        duration: values.duration || 0, // Default to 0 if undefined  
        level: values.level || '', // Ensure it's not undefined  
        price: values.price || '', // Ensure it's not undefined  
        status: values.status || '', // Ensure it's not undefined  
        thumbnail: values.thumbnail || {}, // Default to an empty object if undefined  
        subjects: subjects?.map(subject => ({
          subjectId: subject?.subjectId || '', // Default to an empty string if undefined  
          subjectName: subject?.subjectName || '', // Default to an empty string if undefined  
          moduleIds: subject?.moduleIds || [] // Use an empty array if undefined  
        })),
        introVideo: values.introVideo || '', // Ensure it's not undefined  
        syllabusMaterial: values?.syllabusMaterial || {}, // Use an empty array for undefined  
        enrollmentLimit: values?.enrollmentLimit || 25, // Default to 25 if undefined  
        certificate: values?.certificate || '',// Ensure it's not undefined  
        instructor: values?.instructor,
        rating: Number(values?.rating) ?? 1
      };

      const courseObj = eliminateDuplicateModuleIds(courseData);
      // Update the existing document in Firestore  
      const courseRef = doc(db, "courses", courseId); // Get a reference to the course document  

      // Use setDoc to update the document in Firestore  
      await setDoc(courseRef, courseObj); // Update the course data in Firestore  
      toast({
        title: "Course updated",
        description: `${courseData.courseName} has been successfully updated`,
      });
      navigate(`/courses/${courseIdd}`)
    } catch (error) {
      console.error("Error updating course:", error);
      toast({
        title: "Error",
        description: "Failed to update the course. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function eliminateDuplicateModuleIds(course) {
    course?.subjects?.forEach(subject => {
      const uniqueModuleIds = Array.from(new Set(subject.moduleIds));
      subject.moduleIds = uniqueModuleIds; // Update the subject's moduleIds to unique values  
    });
    return course; // Return the modified course  
  }

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create the thumbnail object with file details  
      const thumbnailObject = {
        fileName: Date.now().toString() + file.name,
        size: file.size,
        url: URL.createObjectURL(file) // Create a temporary URL for the file  
      };

      // Set the thumbnail object in the form  
      form.setValue('thumbnail', thumbnailObject);

      const reader = new FileReader();
      reader.onloadend = () => {
        // Make sure the result is a string  
        const result = typeof reader.result === 'string' ? reader.result : '';

        setTempImage(result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSyllabusMaterialChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return; // Exit if no file was selected   

    // Create a thumbnail object with initial file details   
    const syllabusObject = {
      fileName: Date.now().toString() + file.name,
      size: file.size,
      url: URL.createObjectURL(file) // Temporary URL for preview  
    };

    // Prepare for uploading the file to storage  
    const storageRef = ref(storage, `files/${Date.now().toString()}_${file.name}`); // Using "_" for better URL formatting  

    try {
      setLoading(true); // Optional loading indicator  

      // Upload the file to storage  
      await uploadBytes(storageRef, file);

      // Get the download URL after successful upload  
      const url = await getDownloadURL(storageRef);

      // Prepare the final syllabus material object with the URL from storage  
      const syllabusMaterialObj = {
        fileName: file.name,
        size: file.size,
        url: url // Set the URL after the file is uploaded  
      };

      // Update the form's field and state with the new object  
      form.setValue('syllabusMaterial', syllabusMaterialObj);
      setFormData(prev => ({
        ...prev,
        syllabusMaterial: syllabusMaterialObj // Updated syllabus material in state  
      }));

    } catch (uploadError) {
      console.error(uploadError);
      toast({
        title: "Error",
        description: uploadError.message || "Error uploading syllabus material",
        variant: "destructive"
      });
    } finally {
      setLoading(false);     // Hide loading indicator  
    }

    // Load the file for local preview  
    const reader = new FileReader();
    reader.onloadend = () => {
      // You can use the reader.result if needed  
      const result = typeof reader.result === 'string' ? reader.result : '';
      // Do something with `result` if needed, like updating state for preview  
    };
    reader.readAsDataURL(file);
  };


  const clearSyllabusMaterial = async () => {
    setLoading(true);

    try {
      const { syllabusMaterial } = form.getValues(); // Get current form values to access the thumbnail  

      if (syllabusMaterial) {
        // Delete the file from Firebase Storage  
        const fileRef = ref(storage, `files/${syllabusMaterial.fileName}`);
        await deleteObject(fileRef);

        // Delete the file metadata from Firestore  
        const q = query(
          collection(db, "files"),
          where("name", "==", syllabusMaterial.fileName)
        );
        const querySnapshot = await getDocs(q);

        // Use a for...of loop to handle async delete operations  
        for (const doc of querySnapshot.docs) {
          await deleteDoc(doc.ref); // Await the deletion of each document  
        }
      }

      // Clear the thumbnail data from form and internal state  
      setFormData(prev => ({
        ...prev,
        thumbnail: null,
        thumbnailPreview: null
      }));
      form.setValue('syllabusMaterial', null);

    } catch (error) {
      console.error("Error clearing syllabusMaterial:", error);
      toast({
        title: "Error",
        description: "Failed to clear syllabusMaterial. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false); // Hide loader regardless of success or failure  
    }
  }

  const clearThumbnail = async () => {
    setLoading(true);

    try {
      const { thumbnail } = form.getValues(); // Get current form values to access the thumbnail  

      if (thumbnail) {
        // Delete the file from Firebase Storage  
        const fileRef = ref(storage, `files/${thumbnail.fileName}`);
        await deleteObject(fileRef);

        // Delete the file metadata from Firestore  
        const q = query(
          collection(db, "files"),
          where("name", "==", thumbnail.fileName)
        );
        const querySnapshot = await getDocs(q);

        // Use a for...of loop to handle async delete operations  
        for (const doc of querySnapshot.docs) {
          await deleteDoc(doc.ref); // Await the deletion of each document  
        }
      }

      // Clear the thumbnail data from form and internal state  
      setFormData(prev => ({
        ...prev,
        thumbnail: null,
        thumbnailPreview: null
      }));
      form.setValue('thumbnail', null);

    } catch (error) {
      console.error("Error clearing thumbnail:", error);
      toast({
        title: "Error",
        description: "Failed to clear thumbnail. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false); // Hide loader regardless of success or failure  
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    setFormData(prev => ({
      ...prev,
      thumbnail: null, // We will set the thumbnail as an object below  
      thumbnailPreview: croppedImage // This is the preview of the cropped thumbnail  
    }));

    // Create a blob from the cropped image string  
    const response = await fetch(croppedImage);
    const blob = await response.blob();
    const fileName = `${Date.now().toString()}_thumbnail.png`
    const file = new File([blob], fileName, { type: "image/png" }); // Create file from blob  

    // Prepare for uploading thumbnail to storage  
    const storageRef = ref(storage, `files/${fileName}`); // Use a dedicated folder for thumbnails  
    try {
      setLoading(true); // Optionally show a loading indicator  

      // Upload the cropped image file to storage  
      await uploadBytes(storageRef, file);

      // Get download URL after successful upload  
      const url = await getDownloadURL(storageRef);

      // Now we save the thumbnail details in the form data  
      const thumbnailObject = {
        fileName: fileName,
        size: file.size,
        url: url // Set the URL in the thumbnail object  
      };

      // Update the form's thumbnail field with the new object  
      form.setValue('thumbnail', thumbnailObject);

      // Optionally set in state or other variable if you're displaying it elsewhere  
      setFormData(prev => ({
        ...prev,
        thumbnail: thumbnailObject // Updated thumbnail object  
      }));

    } catch (uploadError) {
      console.error(uploadError);
      toast({
        title: "Error",
        description: uploadError.message || "Error uploading thumbnail",
        variant: "destructive"
      });
    } finally {
      setShowCropper(false);
      setTempImage(null);
      setLoading(false); // Hide loader regardless of success or failure  
    }
  };

  // For displaying form errors at the top level  
  const formErrors = form?.formState?.errors;
  const hasErrors = Object.keys(formErrors).length > 0;

  // Determine if the Create button should be enabled  
  const isFormValid = (form?.formState.isValid || !hasErrors) && !loading;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Edit Course" subtitle="Update course details" />
      <div className="mb-0 mt-6 ml-6">
        <Link to={`/courses/`} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Courses
        </Link>
      </div>
      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">
        <Card className="max-w-5xl mx-auto">
          <CardHeader>
            <CardTitle className="text-bytesplash-blue">Edit Course</CardTitle>
            <AnimatePresence>
              {hasErrors && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>
                      Please fix the validation errors before saving.
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>
          </CardHeader>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Tabs
              defaultValue="general"
              value={activeTab}
              onValueChange={(value) => setActiveTab(value)}
            >
              <div className="px-6">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="general">General Information</TabsTrigger>
                  <TabsTrigger value="modules" disabled={!courseId}>
                    Modules
                    {!courseId && (
                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                        Save First
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="media">Media</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="general">
                <GeneralInfoTab
                  formData={formData}
                  handleChange={handleChange}
                  handleSelectChange={handleSelectChange}
                  form={form}
                  categories={courseCategories}
                />
              </TabsContent>

              <TabsContent value="modules" className="px-6">
                {courseId ? (
                  <ModulesTab
                    courseId={courseId}
                    batches={batches ?? []}
                    subjects={subjects}
                    isReadonly={false}
                    allSubjects={allSubjects}
                    onSubjectsChange={onSubjectsChang}
                  />
                ) : (
                  <div className="py-12 text-center">
                    <p className="text-gray-500">
                      Please save the course first to enable module management.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="media">
                <MediaTab
                  formData={formData}
                  handleThumbnailChange={handleThumbnailChange}
                  handleSyllabusMaterialChange={handleSyllabusMaterialChange}
                  clearThumbnail={clearThumbnail}
                  clearSyllabusMaterial={clearSyllabusMaterial}
                  form={form}
                />
              </TabsContent>

              <TabsContent value="settings">
                <SettingsTab form={form} />
              </TabsContent>

              <CardFooter className="flex justify-between px-6 py-4">
                <Button variant="outline" type="button" onClick={() => navigate('/courses')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid} // Enable/disable based on form validity  
                >
                  {loading ? 'Updating...' : 'Update Course'}
                </Button>
              </CardFooter>
            </Tabs>
          </form>
        </Card>
      </div>

      {/* Image Cropper */}
      {tempImage && (
        <ImageCropper
          open={showCropper}
          onClose={() => setShowCropper(false)}
          imageSrc={tempImage}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default EditCourse;  